import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import FlowMap from "@/components/FlowMap";
import { DistanceChart, HourlyChart, PurposeChart } from "@/components/AnalysisCharts";
import HeatmapMatrix from "@/components/HeatmapMatrix";
import AvailableDatePicker from "@/components/AvailableDatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  DownloadSimple,
  FloppyDisk,
  MagnifyingGlass,
  Path,
  Play,
  X,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const LEVEL_OPTIONS = [
  { value: "distrito", label: "Distritos" },
  { value: "municipio", label: "Municipios" },
  { value: "gau", label: "GAU" },
  { value: "provincia", label: "Provincias" },
];
const PERIOD_OPTIONS = [
  { value: "all", label: "Día completo" },
  { value: "madrugada", label: "Madrugada (0–6)" },
  { value: "manana", label: "Mañana (6–12)" },
  { value: "tarde", label: "Tarde (12–18)" },
  { value: "noche", label: "Noche (18–24)" },
];
const PURPOSE_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "trabajo", label: "Trabajo" },
  { value: "estudios", label: "Estudios" },
  { value: "ocio", label: "Ocio" },
  { value: "residencia", label: "Residencia" },
];
const DISTANCE_OPTIONS = [
  { value: "all", label: "Todas" },
  { value: "0-2", label: "0–2 km" },
  { value: "2-10", label: "2–10 km" },
  { value: "10-50", label: "10–50 km" },
  { value: "50+", label: "+50 km" },
];
const MODE_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "coche", label: "Coche" },
  { value: "transporte_publico", label: "Transp. público" },
  { value: "pie_bici", label: "Pie / Bici" },
  { value: "otros", label: "Otros" },
];
const SEX_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "hombre", label: "Hombre" },
  { value: "mujer", label: "Mujer" },
];
const AGE_OPTIONS = [
  { value: "all", label: "Todas" },
  { value: "0-25", label: "0–25" },
  { value: "25-45", label: "25–45" },
  { value: "45-65", label: "45–65" },
  { value: "65+", label: "65+" },
];
const INCOME_OPTIONS = [
  { value: "all", label: "Todas" },
  { value: "10-15", label: "10–15 K€" },
  { value: "15-30", label: "15–30 K€" },
  { value: ">30", label: ">30 K€" },
];
const SOURCE_OPTIONS = [
  { value: "synthetic", label: "Estimación gravitacional" },
  { value: "real", label: "MITMA real (CSV oficial)" },
];

const todayMinusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

function toCSV(cells) {
  const headers = ["origen_id", "origen_nombre", "destino_id", "destino_nombre", "viajes", "distancia_km", "duracion_min"];
  const rows = cells.map((c) =>
    [c.origin_id, c.origin_name, c.destination_id, c.destination_name, c.trips, c.distance_km, c.avg_duration_min]
      .map((v) => `"${String(v).replaceAll('"', '""')}"`)
      .join(",")
  );
  return [headers.join(","), ...rows].join("\n");
}

const provCodeOf = (z) => z.province_code || ((z.id.split("_")[0] || "").slice(0, 2).match(/^\d\d$/) ? z.id.slice(0, 2) : "");

export default function Analysis() {
  const { user } = useAuth();
  const allowedProvs = user?.role !== "admin" ? user?.allowed_provinces || [] : [];
  const [level, setLevel] = useState("distrito");
  const [zones, setZones] = useState([]);
  const [geojson, setGeojson] = useState(null);
  const [search, setSearch] = useState("");
  const [origins, setOrigins] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [mode, setMode] = useState("origin"); // which list a map click adds to
  const [lassoActive, setLassoActive] = useState(false);
  const [date, setDate] = useState(todayMinusDays(60));
  const [availableDates, setAvailableDates] = useState([]);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareDates, setCompareDates] = useState([todayMinusDays(60), todayMinusDays(53)]);
  const [dataSource, setDataSource] = useState("real");
  const [filters, setFilters] = useState({
    period: "all",
    purpose: "all",
    distance_band: "all",
    mode: "all",
    residence_province: "",
    sex: "all",
    age: "all",
    income: "all",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null); // {pct, stage, message} | null
  const [tab, setTab] = useState("matriz");
  const [saveOpen, setSaveOpen] = useState(false);
  const [projectName, setProjectName] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      const [zonesResp, geoResp] = await Promise.all([
        api.get(`/zones?level=${level}&limit=5000`),
        api.get(`/zones/geojson/${level}`),
      ]);
      if (!active) return;
      setZones(zonesResp.data.zones);
      setGeojson(geoResp.data);
    })();
    return () => {
      active = false;
    };
  }, [level]);

  // Fetch the list of dates that actually have data in S3 for the current
  // level. This drives the calendar so the user can only pick days with
  // available CSVs.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/data/available-dates?level=${level}`);
        if (!active) return;
        const dates = data?.dates || [];
        setAvailableDates(dates);
        // If the currently selected dates are not in the new available set,
        // snap them to the most recent available date so the user always
        // has a valid selection.
        if (dates.length > 0) {
          const setIso = new Set(dates);
          const last = dates[dates.length - 1];
          if (!setIso.has(date)) setDate(last);
          setCompareDates((arr) => arr.map((d) => (setIso.has(d) ? d : last)));
        }
      } catch (_) {
        if (active) setAvailableDates([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [level]);

  useEffect(() => {
    // when level changes, clear selections that don't belong
    setOrigins((arr) => arr.filter((id) => zones.some((z) => z.id === id)));
    setDestinations((arr) => arr.filter((id) => zones.some((z) => z.id === id)));
  }, [zones]);

  const zoneById = useMemo(() => new Map(zones.map((z) => [z.id, z])), [zones]);

  const filteredZones = useMemo(() => {
    if (!search) return zones.slice(0, 60);
    const q = search.toLowerCase();
    return zones.filter((z) => z.name.toLowerCase().includes(q) || z.id.includes(q)).slice(0, 60);
  }, [search, zones]);

  const toggleZone = (z) => {
    const target = mode === "origin" ? origins : destinations;
    const setTarget = mode === "origin" ? setOrigins : setDestinations;
    setTarget(target.includes(z.id) ? target.filter((id) => id !== z.id) : [...target, z.id]);
  };

  // Submit an async job and poll its status until it's done or it errors.
  // Returns the final job result payload.
  const submitJob = async (path, payload) => {
    const { data: created } = await api.post(path, payload);
    const jobId = created.job_id;
    setProgress({ pct: 1, stage: "queued", message: "En cola…" });
    let attempts = 0;
    // Up to ~10 min total (400 attempts * 1.5 s) – downloads are typically
    // 20–60 s so this is a wide safety margin.
    while (attempts < 400) {
      await new Promise((r) => setTimeout(r, 1500));
      attempts += 1;
      try {
        const { data } = await api.get(`/od-matrix/jobs/${jobId}`);
        setProgress({ pct: data.progress || 0, stage: data.stage || "", message: data.message || "" });
        if (data.status === "done") return data.result;
        if (data.status === "error") {
          throw new Error(data.error || "Error en el cálculo");
        }
      } catch (e) {
        if (e.response?.status === 404) {
          throw new Error("El job expiró. Vuelve a intentarlo.");
        }
        // transient network blips: keep polling
      }
    }
    throw new Error("Tiempo de espera agotado");
  };

  const run = async () => {
    if (!origins.length || !destinations.length) {
      toast.error("Selecciona al menos un origen y un destino");
      return;
    }
    if (allowedProvs.length) {
      const inside = (id) => allowedProvs.includes(provCodeOf(zoneById.get(id) || { id }));
      if (!origins.every(inside) && !destinations.every(inside)) {
        toast.error(`Tu acceso está limitado a viajes con origen o destino en ${allowedNames}. Todos los orígenes o todos los destinos deben pertenecer a esa zona.`, { duration: 8000 });
        return;
      }
    }
    setLoading(true);
    setProgress({ pct: 0, stage: "init", message: "Iniciando…" });
    try {
      if (compareEnabled) {
        const validDates = compareDates.filter(Boolean);
        if (validDates.length < 2) {
          toast.error("Selecciona al menos 2 fechas para comparar");
          setLoading(false);
          setProgress(null);
          return;
        }
        const data = await submitJob("/od-matrix/compare/async", {
          level,
          origins,
          destinations,
          dates: validDates,
          data_source: dataSource,
          filters: { ...filters, residence_province: filters.residence_province || null },
        });
        const primary = data.results[0];
        setResult({ ...primary, compare: data });
        setTab("comparar");
        toast.success(`${validDates.length} fechas calculadas`);
      } else {
        const data = await submitJob("/od-matrix/async", {
          level,
          origins,
          destinations,
          date,
          data_source: dataSource,
          filters: { ...filters, residence_province: filters.residence_province || null },
        });
        setResult(data);
        if (data.fallback_reason) {
          toast.warning(`Estimación. ${data.fallback_reason}`);
        } else {
          toast.success(
            `${data.summary.total_trips.toLocaleString("es-ES")} viajes · ${data.source === "real" ? "MITMA real" : "estimación"}`
          );
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Error calculando la matriz");
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const downloadCSV = () => {
    if (!result) return;
    const csv = toCSV(result.cells);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `matriz_OD_${level}_${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveProject = async () => {
    if (!projectName.trim()) return;
    try {
      await api.post("/projects", {
        name: projectName.trim(),
        description: `${origins.length} → ${destinations.length} · ${date}`,
        config: { level, origins, destinations, date, filters },
      });
      toast.success("Proyecto guardado");
      setSaveOpen(false);
      setProjectName("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    }
  };

  const flowsForMap = result?.cells.filter((c) => c.trips > 0) || [];

  const handleLassoSelect = (insideZones) => {
    if (insideZones.length === 0) {
      toast.info("Ninguna zona dentro del rectángulo");
      return;
    }
    const ids = insideZones.map((z) => z.id);
    if (mode === "origin") {
      setOrigins(Array.from(new Set([...origins, ...ids])));
    } else {
      setDestinations(Array.from(new Set([...destinations, ...ids])));
    }
    toast.success(`${ids.length} zonas añadidas a ${mode === "origin" ? "orígenes" : "destinos"}`);
    setLassoActive(false);
  };

  // Add all zones of a given province to the current target list
  const addAllOfProvince = (provinceCode) => {
    if (!provinceCode) return;
    if (level === "provincia") {
      toast.info("Cambia a Distrito o Municipio para expandir una provincia");
      return;
    }
    const matches = zones.filter((z) => provCodeOf(z) === provinceCode);
    if (matches.length === 0) {
      toast.error("No se encontraron zonas en esa provincia");
      return;
    }
    const ids = matches.map((z) => z.id);
    if (mode === "origin") {
      setOrigins(Array.from(new Set([...origins, ...ids])));
    } else {
      setDestinations(Array.from(new Set([...destinations, ...ids])));
    }
    toast.success(`${ids.length} zonas añadidas a ${mode === "origin" ? "orígenes" : "destinos"}`);
  };

  // List of provinces derived from currently loaded zones (level-aware)
  const provinceList = useMemo(() => {
    const m = new Map();
    zones.forEach((z) => {
      const code = provCodeOf(z);
      if (code && !m.has(code)) m.set(code, z.province || code);
    });
    return Array.from(m.entries())
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [zones]);

  const allowedNames = allowedProvs
    .map((c) => provinceList.find((p) => p.code === c)?.name || c)
    .join(", ");

  return (
    <>
    <div className="h-full grid" style={{ gridTemplateColumns: "320px 1fr 380px" }}>
      {/* LEFT PANE — SELECTION + FILTERS */}
      <div className="border-r border-white/10 bg-[#0A0A0A] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <p className="label-micro">SELECCIÓN</p>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="h-7 w-[120px] rounded-none border-white/10 bg-[#121212] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LEVEL_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {allowedProvs.length > 0 && (
          <div className="px-4 py-2 border-b border-[#00E5FF]/20 bg-[#00E5FF]/[0.06] text-[11px] leading-snug" data-testid="access-restriction-banner">
            <span className="label-micro text-[#00E5FF]">ACCESO LIMITADO</span>
            <p className="text-white/70 mt-0.5">
              Consultas con origen o destino en <span className="text-white font-semibold">{allowedNames}</span>
              {user?.valid_until && <> · hasta {new Date(user.valid_until + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" })}</>}
            </p>
          </div>
        )}
        <Tabs defaultValue="buscar" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="w-full justify-start bg-transparent border-b border-white/10 rounded-none px-2 pt-2">
            <TabsTrigger
              value="buscar"
              className="rounded-none data-[state=active]:bg-[#1A1A1A] data-[state=active]:border-b-2 data-[state=active]:border-[#FF9800]"
            >
              Buscar
            </TabsTrigger>
            <TabsTrigger
              value="seleccion"
              className="rounded-none data-[state=active]:bg-[#1A1A1A] data-[state=active]:border-b-2 data-[state=active]:border-[#FF9800]"
              data-testid="tab-selection"
            >
              Selección
            </TabsTrigger>
          </TabsList>

          <TabsContent value="buscar" className="flex-1 m-0 overflow-hidden flex flex-col">
            <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
              <MagnifyingGlass size={14} className="text-white/40" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar zona o código…"
                data-testid="zone-search-input"
                className="h-7 rounded-none border-white/10 bg-[#121212] text-xs"
              />
            </div>
            <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
              <ModeToggle mode={mode} setMode={setMode} />
            </div>
            <div className="px-3 py-2 border-b border-white/10 flex items-center gap-1.5">
              <span className="label-micro text-white/40 shrink-0">PROVINCIA</span>
              <Select
                value=""
                onValueChange={(v) => addAllOfProvince(v)}
                disabled={level === "provincia" || provinceList.length === 0}
              >
                <SelectTrigger
                  className="h-7 rounded-none border-white/10 bg-[#121212] text-xs flex-1"
                  data-testid="province-expander-select"
                >
                  <SelectValue placeholder={level === "provincia" ? "(no aplicable)" : "Añadir todas las zonas…"} />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {provinceList.map((p) => (
                    <SelectItem key={p.code} value={p.code} data-testid={`province-add-${p.code}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 overflow-auto" data-testid="zone-search-results">
              {filteredZones.map((z) => {
                const isO = origins.includes(z.id);
                const isD = destinations.includes(z.id);
                return (
                  <button
                    key={z.id}
                    onClick={() => toggleZone(z)}
                    data-testid={`zone-result-${z.id}`}
                    className={`w-full text-left px-3 py-2 border-b border-white/5 hover:bg-white/[0.03] flex items-center gap-2 ${
                      isO || isD ? "bg-white/[0.02]" : ""
                    }`}
                  >
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm text-white truncate">{z.name}</span>
                      <span className="font-mono text-[10px] text-white/40">{z.id} · {z.province}</span>
                    </div>
                    {isO && <span className="font-mono text-[10px] text-[#00E5FF]">O</span>}
                    {isD && <span className="font-mono text-[10px] text-[#FF5722]">D</span>}
                  </button>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="seleccion" className="flex-1 m-0 overflow-auto p-3 space-y-4">
            <SelectionList
              title="ORÍGENES"
              color="#00E5FF"
              ids={origins}
              zoneById={zoneById}
              onRemove={(id) => setOrigins(origins.filter((x) => x !== id))}
              icon={<ArrowUp size={12} weight="bold" />}
            />
            <SelectionList
              title="DESTINOS"
              color="#FF5722"
              ids={destinations}
              zoneById={zoneById}
              onRemove={(id) => setDestinations(destinations.filter((x) => x !== id))}
              icon={<ArrowDown size={12} weight="bold" />}
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setOrigins([]);
                  setDestinations([]);
                }}
                data-testid="clear-selection-btn"
                className="rounded-none border-white/10 bg-transparent text-white/60 hover:text-white hover:bg-white/[0.04] flex-1 text-xs h-8"
              >
                Limpiar
              </Button>
              <Button
                variant="outline"
                onClick={() => setDestinations([...origins])}
                data-testid="copy-origins-to-destinations-btn"
                disabled={!origins.length}
                className="rounded-none border-white/10 bg-transparent text-white/60 hover:text-white hover:bg-white/[0.04] flex-1 text-xs h-8"
              >
                D = O
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* FILTERS */}
        <div className="border-t border-white/10 p-3 space-y-3 bg-[#0A0A0A]">
          <p className="label-micro">FILTROS</p>
          <FilterRow label="Fecha">
            <AvailableDatePicker
              value={date}
              onChange={setDate}
              availableDates={availableDates}
              testId="date-input"
              disabled={compareEnabled}
            />
          </FilterRow>
          <div className="flex items-center justify-between px-1">
            <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-white/60 cursor-pointer">
              <input
                type="checkbox"
                checked={compareEnabled}
                onChange={(e) => setCompareEnabled(e.target.checked)}
                className="accent-[#FF9800]"
                data-testid="compare-toggle"
              />
              Comparar fechas
            </label>
            {compareEnabled && (
              <button
                onClick={() => setCompareDates([...compareDates, todayMinusDays(30)])}
                disabled={compareDates.length >= 5}
                className="text-[10px] font-mono text-[#00E5FF] hover:text-white disabled:opacity-30"
                data-testid="add-date-btn"
              >
                + AÑADIR
              </button>
            )}
          </div>
          {compareEnabled && (
            <div className="space-y-1.5 pl-2 border-l border-[#00E5FF]/40">
              {compareDates.map((d, i) => (
                <div key={i} className="flex items-center gap-1">
                  <span className="label-micro text-white/40 w-4">{i + 1}</span>
                  <div className="flex-1">
                    <AvailableDatePicker
                      value={d}
                      onChange={(nv) => {
                        const arr = [...compareDates];
                        arr[i] = nv;
                        setCompareDates(arr);
                      }}
                      availableDates={availableDates}
                      testId={`compare-date-${i}`}
                    />
                  </div>
                  {compareDates.length > 2 && (
                    <button
                      onClick={() => setCompareDates(compareDates.filter((_, j) => j !== i))}
                      className="text-white/30 hover:text-[#F44336]"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          <FilterRow label="Periodo">
            <FilterSelect value={filters.period} onValueChange={(v) => setFilters({ ...filters, period: v })} options={PERIOD_OPTIONS} />
          </FilterRow>
          <FilterRow label="Motivo">
            <FilterSelect value={filters.purpose} onValueChange={(v) => setFilters({ ...filters, purpose: v })} options={PURPOSE_OPTIONS} />
          </FilterRow>
          <FilterRow label="Distancia">
            <FilterSelect value={filters.distance_band} onValueChange={(v) => setFilters({ ...filters, distance_band: v })} options={DISTANCE_OPTIONS} />
          </FilterRow>
          <FilterRow label="Modo">
            <FilterSelect value={filters.mode} onValueChange={(v) => setFilters({ ...filters, mode: v })} options={MODE_OPTIONS} />
          </FilterRow>
          <FilterRow label="Sexo">
            <FilterSelect value={filters.sex} onValueChange={(v) => setFilters({ ...filters, sex: v })} options={SEX_OPTIONS} />
          </FilterRow>
          <FilterRow label="Edad">
            <FilterSelect value={filters.age} onValueChange={(v) => setFilters({ ...filters, age: v })} options={AGE_OPTIONS} />
          </FilterRow>
          <FilterRow label="Renta">
            <FilterSelect value={filters.income} onValueChange={(v) => setFilters({ ...filters, income: v })} options={INCOME_OPTIONS} />
          </FilterRow>

          <Button
            onClick={run}
            disabled={loading}
            data-testid="run-analysis-btn"
            className="w-full h-10 rounded-none bg-[#FF9800] hover:bg-[#FFA726] text-black font-semibold btn-press"
          >
            {loading
              ? "Procesando…"
              : (<><Play size={14} weight="fill" className="mr-2" />{compareEnabled ? "Comparar fechas" : "Calcular matriz"}</>)}
          </Button>

          {loading && progress && (
            <div className="mt-2" data-testid="job-progress">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                  {progress.stage || "procesando"}
                </span>
                <span className="font-mono text-[10px] text-[#FF9800] tabular-nums">
                  {(progress.pct || 0).toFixed(1)}%
                </span>
              </div>
              <div className="h-1.5 w-full bg-white/5 overflow-hidden">
                <div
                  className="h-full bg-[#FF9800] transition-all duration-300 ease-out"
                  style={{ width: `${Math.min(100, Math.max(2, progress.pct || 2))}%` }}
                />
              </div>
              {progress.message && (
                <p className="font-mono text-[10px] text-white/40 mt-1 line-clamp-2 break-all">
                  {progress.message}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* CENTER MAP */}
      <div className="relative overflow-hidden">
        <FlowMap
          zones={zones}
          geojson={geojson}
          origins={origins}
          destinations={destinations}
          flows={tab === "flujos" ? flowsForMap : []}
          onToggleZone={toggleZone}
          lassoActive={lassoActive}
          onLassoSelect={handleLassoSelect}
        />
        <div className="absolute top-4 left-4 z-[1000] border border-white/10 bg-[#0A0A0A]/90 backdrop-blur-md px-3 py-2 flex items-center gap-3">
          <ModeToggle mode={mode} setMode={setMode} />
          <span className="w-px h-4 bg-white/10" />
          <button
            data-testid="lasso-btn"
            onClick={() => setLassoActive((v) => !v)}
            className={`px-2 py-1 border text-[10px] font-mono uppercase tracking-[0.2em] ${
              lassoActive
                ? "border-[#FF9800] text-[#FF9800] bg-[#FF9800]/10"
                : "border-white/10 text-white/50 hover:text-white"
            }`}
            title="Arrastra un rectángulo en el mapa para seleccionar varias zonas"
          >
            Rectángulo
          </button>
        </div>
        <div className="absolute top-4 right-4 z-[1000] border border-white/10 bg-[#0A0A0A]/90 backdrop-blur-md px-3 py-2 text-[10px] font-mono uppercase tracking-[0.2em] text-white/60 flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full border-2 border-[#00E5FF] bg-[#00E5FF]/30" /> Origen
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full border-2 border-[#FF5722] bg-[#FF5722]/30" /> Destino
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3.5 h-[2px] bg-[#FF5722]" /> Flujo
          </span>
        </div>
        {result && (
          <div className="absolute bottom-4 left-4 z-[1000] border border-white/10 bg-[#0A0A0A]/90 backdrop-blur-md px-4 py-3 font-mono">
            <div className="flex items-center justify-between gap-6">
              <p className="label-micro text-[#FF9800]">RESULTADO</p>
              <span
                className={`label-micro px-2 py-0.5 border ${
                  result.source === "real"
                    ? "border-[#00E5FF] text-[#00E5FF]"
                    : "border-white/20 text-white/50"
                }`}
                data-testid="result-source-badge"
              >
                {result.source === "real" ? "MITMA REAL" : "ESTIMACIÓN"}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-6 text-sm mt-1">
              <div>
                <p className="text-white/40 text-[10px] uppercase">Viajes</p>
                <p className="text-white text-lg" data-testid="result-trips">{result.summary.total_trips.toLocaleString("es-ES")}</p>
              </div>
              <div>
                <p className="text-white/40 text-[10px] uppercase">Km totales</p>
                <p className="text-white text-lg">{result.summary.total_km.toLocaleString("es-ES")}</p>
              </div>
              <div>
                <p className="text-white/40 text-[10px] uppercase">Dist. media</p>
                <p className="text-white text-lg">{result.summary.avg_distance_km} km</p>
              </div>
            </div>
            {result.fallback_reason && (
              <p className="text-[10px] text-[#FFB74D] mt-2 max-w-md">{result.fallback_reason}</p>
            )}
          </div>
        )}
      </div>

      {/* RIGHT PANE */}
      <div className="border-l border-white/10 bg-[#0A0A0A] flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <p className="label-micro">RESULTADOS + KPIs</p>
          <div className="flex gap-1">
            <button
              onClick={() => setSaveOpen(true)}
              disabled={!origins.length || !destinations.length}
              data-testid="save-project-btn"
              title="Guardar proyecto"
              className="p-1.5 border border-white/10 hover:border-[#FF9800] btn-press disabled:opacity-30"
            >
              <FloppyDisk size={14} />
            </button>
            <button
              onClick={downloadCSV}
              disabled={!result}
              data-testid="download-csv-btn"
              title="Descargar CSV"
              className="p-1.5 border border-white/10 hover:border-[#FF9800] btn-press disabled:opacity-30"
            >
              <DownloadSimple size={14} />
            </button>
          </div>
        </div>
        <KpiStrip result={result} originsCount={origins.length} destinationsCount={destinations.length} zoneById={zoneById} origins={origins} destinations={destinations} />

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="w-full justify-start bg-transparent border-b border-white/10 rounded-none px-2 pt-2 overflow-x-auto">
            <TabsTrigger value="matriz" className="rounded-none data-[state=active]:bg-[#1A1A1A] data-[state=active]:border-b-2 data-[state=active]:border-[#FF9800]" data-testid="tab-matriz">
              Matriz
            </TabsTrigger>
            <TabsTrigger value="flujos" className="rounded-none data-[state=active]:bg-[#1A1A1A] data-[state=active]:border-b-2 data-[state=active]:border-[#FF9800]" data-testid="tab-flujos">
              Flujos
            </TabsTrigger>
            <TabsTrigger value="heatmap" className="rounded-none data-[state=active]:bg-[#1A1A1A] data-[state=active]:border-b-2 data-[state=active]:border-[#FF9800]" data-testid="tab-heatmap">
              Heatmap
            </TabsTrigger>
            <TabsTrigger value="graficos" className="rounded-none data-[state=active]:bg-[#1A1A1A] data-[state=active]:border-b-2 data-[state=active]:border-[#FF9800]" data-testid="tab-graficos">
              Gráficos
            </TabsTrigger>
            <TabsTrigger value="comparar" className="rounded-none data-[state=active]:bg-[#1A1A1A] data-[state=active]:border-b-2 data-[state=active]:border-[#00E5FF]" data-testid="tab-comparar" disabled={!result?.compare}>
              Comparar
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-auto p-3">
            {!result && (
              <EmptyState />
            )}
            {result && (
              <>
                <TabsContent value="matriz" className="mt-0">
                  <MatrixTable cells={result.cells} />
                </TabsContent>
                <TabsContent value="flujos" className="mt-0 space-y-3">
                  <FlowList cells={result.cells} />
                </TabsContent>
                <TabsContent value="heatmap" className="mt-0">
                  <HeatmapMatrix
                    cells={result.cells}
                    origins={origins}
                    destinations={destinations}
                    zoneById={zoneById}
                  />
                </TabsContent>
                <TabsContent value="graficos" className="mt-0 space-y-3">
                  <HourlyChart data={result.by_hour} />
                  <DistanceChart data={result.by_distance_band} />
                  <PurposeChart data={result.by_purpose} />
                </TabsContent>
                <TabsContent value="comparar" className="mt-0">
                  {result.compare ? <CompareView compare={result.compare} /> : (
                    <p className="text-white/40 text-sm">Activa "Comparar fechas" en los filtros y elige al menos 2 fechas.</p>
                  )}
                </TabsContent>
              </>
            )}
          </div>
        </Tabs>
      </div>
    </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white rounded-none">
          <DialogHeader>
            <DialogTitle>Guardar análisis como proyecto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="label-micro">Nombre</Label>
            <Input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Movilidad Madrid Centro · marzo"
              className="rounded-none bg-[#121212] border-white/10"
              data-testid="project-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)} className="rounded-none border-white/10 bg-transparent">
              Cancelar
            </Button>
            <Button onClick={saveProject} className="rounded-none bg-[#FF9800] text-black hover:bg-[#FFA726]" data-testid="confirm-save-project-btn">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ModeToggle({ mode, setMode }) {
  return (
    <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.2em]">
      <span className="text-white/40">Clic asigna →</span>
      <button
        onClick={() => setMode("origin")}
        data-testid="mode-origin-btn"
        className={`px-2 py-1 border ${mode === "origin" ? "border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/10" : "border-white/10 text-white/50 hover:text-white"}`}
      >
        Origen
      </button>
      <button
        onClick={() => setMode("destination")}
        data-testid="mode-destination-btn"
        className={`px-2 py-1 border ${mode === "destination" ? "border-[#FF5722] text-[#FF5722] bg-[#FF5722]/10" : "border-white/10 text-white/50 hover:text-white"}`}
      >
        Destino
      </button>
    </div>
  );
}

function FilterRow({ label, children }) {
  return (
    <div className="grid grid-cols-[80px_1fr] gap-2 items-center">
      <span className="label-micro text-white/40">{label}</span>
      <div>{children}</div>
    </div>
  );
}

function FilterSelect({ value, onValueChange, options }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="h-8 rounded-none border-white/10 bg-[#121212] text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SelectionList({ title, color, ids, zoneById, onRemove, icon }) {
  return (
    <div>
      <p className="label-micro flex items-center gap-2 mb-2" style={{ color }}>
        {icon}
        {title} · {ids.length}
      </p>
      <div className="space-y-1">
        {ids.length === 0 && (
          <p className="text-white/30 text-xs">Ninguno seleccionado</p>
        )}
        {ids.map((id) => {
          const z = zoneById.get(id);
          return (
            <div key={id} className="flex items-center justify-between gap-2 px-2 py-1.5 bg-[#121212] border border-white/5">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-white truncate">{z?.name || id}</p>
                <p className="font-mono text-[10px] text-white/40">{id}</p>
              </div>
              <button onClick={() => onRemove(id)} className="text-white/40 hover:text-[#F44336]">
                <X size={12} weight="bold" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MatrixTable({ cells }) {
  const sorted = [...cells].sort((a, b) => b.trips - a.trips);
  return (
    <div className="border border-white/10 bg-[#121212]" data-testid="matrix-table">
      <table className="w-full text-xs font-mono">
        <thead className="bg-[#0A0A0A] sticky top-0">
          <tr className="border-b border-white/10">
            <th className="text-left px-2 py-2 label-micro">ORIGEN</th>
            <th className="text-left px-2 py-2 label-micro">DESTINO</th>
            <th className="text-right px-2 py-2 label-micro">VIAJES</th>
            <th className="text-right px-2 py-2 label-micro">KM</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((c, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/[0.03]">
              <td className="px-2 py-1.5 text-white/80 truncate max-w-[140px]" title={c.origin_name}>{c.origin_name}</td>
              <td className="px-2 py-1.5 text-white/80 truncate max-w-[140px]" title={c.destination_name}>{c.destination_name}</td>
              <td className="px-2 py-1.5 text-right text-[#FF9800]">{Number(c.trips).toLocaleString("es-ES")}</td>
              <td className="px-2 py-1.5 text-right text-white/60">{c.distance_km}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FlowList({ cells }) {
  const sorted = [...cells].filter((c) => c.origin_id !== c.destination_id).sort((a, b) => b.trips - a.trips).slice(0, 25);
  const max = sorted[0]?.trips || 1;
  return (
    <div className="space-y-2">
      <p className="label-micro">TOP 25 FLUJOS</p>
      {sorted.map((c, i) => (
        <div key={i} className="border border-white/5 bg-[#121212] p-2">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-[#00E5FF] truncate max-w-[40%]" title={c.origin_name}>{c.origin_name}</span>
            <Path size={12} className="text-white/30" />
            <span className="text-[#FF5722] truncate max-w-[40%] text-right" title={c.destination_name}>{c.destination_name}</span>
          </div>
          <div className="h-1.5 bg-white/5 relative">
            <div className="h-full bg-[#FF9800]" style={{ width: `${(c.trips / max) * 100}%` }} />
          </div>
          <div className="flex justify-between mt-1 font-mono text-[10px] text-white/40">
            <span>{c.distance_km} km</span>
            <span className="text-[#FF9800]">{Number(c.trips).toLocaleString("es-ES")} viajes</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-white/10 p-8 text-center mt-8">
      <p className="label-micro text-white/60 mb-2">SIN RESULTADOS</p>
      <p className="text-sm text-white/40">
        Selecciona orígenes y destinos y pulsa <span className="text-[#FF9800]">Calcular matriz</span> para empezar.
      </p>
    </div>
  );
}

function CompareView({ compare }) {
  const { results, deltas } = compare;
  return (
    <div className="space-y-4" data-testid="compare-view">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {results.map((r) => (
          <div key={r.date} className="border border-white/10 bg-[#121212] p-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{r.date}</p>
            <p className="text-2xl font-bold font-mono mt-1" data-testid={`compare-total-${r.date}`}>
              {Number(r.summary?.total_trips || 0).toLocaleString("es-ES")}
            </p>
            <p className="text-[10px] text-white/40 font-mono">viajes totales</p>
          </div>
        ))}
      </div>

      {deltas.map((d, i) => (
        <div key={i} className="border border-[#00E5FF]/30 bg-[#121212]">
          <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between">
            <p className="font-mono text-xs">
              <span className="text-[#FF9800]">{d.baseline_date}</span>
              <span className="text-white/40 mx-2">→</span>
              <span className="text-[#00E5FF]">{d.compare_date}</span>
            </p>
            <p className={`font-mono text-sm font-bold ${d.delta_total >= 0 ? "text-[#00E5FF]" : "text-[#FF5722]"}`}>
              {d.delta_total >= 0 ? "+" : ""}{Number(d.delta_total).toLocaleString("es-ES")}
            </p>
          </div>
          <div className="max-h-72 overflow-auto">
            <table className="w-full text-xs font-mono">
              <thead className="bg-[#0A0A0A] sticky top-0">
                <tr className="border-b border-white/10">
                  <th className="text-left px-2 py-1.5 label-micro">FLUJO</th>
                  <th className="text-right px-2 py-1.5 label-micro">BASE</th>
                  <th className="text-right px-2 py-1.5 label-micro">COMP</th>
                  <th className="text-right px-2 py-1.5 label-micro">Δ</th>
                  <th className="text-right px-2 py-1.5 label-micro">%</th>
                </tr>
              </thead>
              <tbody>
                {d.cells.slice(0, 30).map((c, j) => (
                  <tr key={j} className="border-b border-white/5">
                    <td className="px-2 py-1 text-white/80 truncate max-w-[180px]" title={`${c.origin_name} → ${c.destination_name}`}>
                      {c.origin_name} → {c.destination_name}
                    </td>
                    <td className="px-2 py-1 text-right text-white/60">
                      {Number(c.baseline_trips).toLocaleString("es-ES")}
                    </td>
                    <td className="px-2 py-1 text-right text-white/60">
                      {Number(c.compare_trips).toLocaleString("es-ES")}
                    </td>
                    <td className={`px-2 py-1 text-right font-bold ${c.delta_trips >= 0 ? "text-[#00E5FF]" : "text-[#FF5722]"}`}>
                      {c.delta_trips >= 0 ? "+" : ""}{Number(c.delta_trips).toLocaleString("es-ES")}
                    </td>
                    <td className={`px-2 py-1 text-right ${c.delta_pct >= 0 ? "text-[#00E5FF]" : "text-[#FF5722]"}`}>
                      {c.delta_pct >= 0 ? "+" : ""}{c.delta_pct}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}


function formatInt(n) {
  return Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 0 });
}

function formatDecimal(n, d = 2) {
  return Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: d, maximumFractionDigits: d });
}

function KpiStrip({ result, origins, destinations, zoneById }) {
  // Compute fallback population from zones meta in case the backend didn't send KPIs.
  const popO = useMemo(() =>
    origins.reduce((acc, id) => acc + Number(zoneById.get(id)?.population || 0), 0),
    [origins, zoneById]
  );
  const popD = useMemo(() =>
    destinations.reduce((acc, id) => acc + Number(zoneById.get(id)?.population || 0), 0),
    [destinations, zoneById]
  );

  const summary = result?.summary || {};
  const totalTrips = Number(summary.total_trips || 0);
  const popOrigin = Number(summary.population_origin || popO);
  const popDest = Number(summary.population_destination || popD);
  const tripsPerCapita = popOrigin > 0
    ? Number(summary.trips_per_capita_origin ?? totalTrips / popOrigin)
    : 0;
  const tripsPer1000 = popOrigin > 0
    ? Number(summary.trips_per_1000_origin ?? (totalTrips * 1000 / popOrigin))
    : 0;
  const mobilityRate = popOrigin > 0
    ? Number(summary.mobility_rate_pct ?? Math.min(totalTrips / popOrigin * 100, 100))
    : 0;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/5 border-b border-white/10" data-testid="kpi-strip">
      <Kpi label="Pob. orígenes" value={formatInt(popOrigin)} accent="#00E5FF" testId="kpi-pop-origin" />
      <Kpi label="Pob. destinos" value={formatInt(popDest)} accent="#FF5722" testId="kpi-pop-destination" />
      <Kpi label="Viajes totales" value={formatInt(totalTrips)} accent="#FF9800" testId="kpi-total-trips" />
      <Kpi label="Viajes/hab. orig." value={formatDecimal(tripsPerCapita, 2)} accent="#FFFFFF" testId="kpi-trips-per-capita" />
      <Kpi label="Viajes/1000 hab." value={formatDecimal(tripsPer1000, 1)} accent="#FFFFFF" testId="kpi-trips-per-1000" />
      <Kpi label="Tasa movilidad" value={`${formatDecimal(mobilityRate, 1)}%`} accent="#FFFFFF" testId="kpi-mobility-rate" />
    </div>
  );
}

function Kpi({ label, value, accent = "#FFFFFF", testId }) {
  return (
    <div className="px-3 py-2 bg-[#0A0A0A]" data-testid={testId}>
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/40 truncate">{label}</p>
      <p className="font-mono text-base font-bold truncate" style={{ color: accent }}>
        {value}
      </p>
    </div>
  );
}
